import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useWorkOrderContext } from '../../contexts/WorkOrderContext'
import type { TagStatus } from '../../types/tag'
import type { WorkOrder } from '../../types/workorder'
import {
  getAssignedPersonName,
  getDisplayNameByUserId,
} from '../../utils/relationshipHelpers'
import StatusBadge from '../StatusBadge'
import TruncatableCell from '../TruncatableCell'
import ViewWorkOrderModal from './ViewWorkOrderModal'

interface WorkOrderSubRowProps {
  workOrders: WorkOrder[]
  projectId: string
  columnsCount: number
}

const WorkOrderSubRow: React.FC<WorkOrderSubRowProps> = ({
  workOrders,
  projectId,
  columnsCount,
}) => {
  const navigate = useNavigate()
  const [viewingWorkOrder, setViewingWorkOrder] = useState<WorkOrder | null>(
    null,
  )
  const [creatorNames, setCreatorNames] = useState<Record<number, string>>({})
  const [subhaulerNames, setSubhaulerNames] = useState<Record<number, string>>(
    {},
  )
  const [dispatcherNames, setDispatcherNames] = useState<
    Record<number, string>
  >({})
  const [isExpanded, setIsExpanded] = useState<boolean>(false)

  // Memoize the expensive filtering operation
  const relatedWorkOrders = useMemo(() => {
    return workOrders.filter((workOrder) => {
      const workOrderProjectId =
        typeof workOrder.project === 'object' && workOrder.project
          ? (workOrder.project as { id: number }).id
          : workOrder.project

      return String(workOrderProjectId) === projectId
    })
  }, [workOrders, projectId])

  // Fetch creator names for work orders
  useEffect(() => {
    const fetchCreatorNames = async () => {
      // Since created_by is now a User object, we can directly extract the names
      const creatorMap: Record<number, string> = {}

      for (const wo of relatedWorkOrders) {
        if (wo.created_by && typeof wo.created_by === 'object') {
          const fullName =
            `${wo.created_by.first_name} ${wo.created_by.last_name}`.trim()
          creatorMap[wo.created_by.id] =
            fullName || wo.created_by.email || 'Unknown'
        }
      }

      setCreatorNames(creatorMap)
    }

    if (relatedWorkOrders.length > 0) {
      fetchCreatorNames()
    }
  }, [relatedWorkOrders])

  // Early return if no work orders or no related work orders
  if (
    !workOrders ||
    workOrders.length === 0 ||
    relatedWorkOrders.length === 0
  ) {
    return null
  }

  // Add error check for malformed work order data
  const hasInvalidWorkOrders = relatedWorkOrders.some(
    (workOrder) =>
      (!workOrder.subhaulers || workOrder.subhaulers.length === 0) &&
      (!workOrder.dispatchers || workOrder.dispatchers.length === 0) &&
      !workOrder.created_by,
  )

  if (hasInvalidWorkOrders) {
    console.error('Invalid dispatch data found:', relatedWorkOrders)
    return (
      <tr>
        <td colSpan={columnsCount}>
          <div className={css(styles.errorMessage)}>
            <h3>Error: Invalid Dispatch Data</h3>
            <p>
              One or more dispatches are missing required information. Please
              contact support.
            </p>
          </div>
        </td>
      </tr>
    )
  }

  const handleWorkOrderAction = (workOrder: WorkOrder) => {
    const status = workOrder.status?.toUpperCase()

    if (status === 'PENDING' || status === 'NEW') {
      setViewingWorkOrder(workOrder)
    } else if (status === 'ACCEPTED') {
      navigate('/work-orders/accepted')
    } else if (status === 'COMPLETE') {
      navigate('/work-orders/accepted')
    } else {
      setViewingWorkOrder(workOrder)
    }
  }

  const isButtonDisabled = (workOrder: WorkOrder) => {
    // No buttons should be disabled - all work orders are viewable
    return false
  }

  // Enhanced sub haulers name display with fallback
  const getSubHaulersName = (workOrder: WorkOrder) => {
    // NEW: Display multiple subhaulers
    if (workOrder.subhaulers && workOrder.subhaulers.length > 0) {
      return workOrder.subhaulers
        .map((user) => {
          const fullName = `${user.first_name} ${user.last_name}`.trim()
          return fullName || user.email || 'Unknown'
        })
        .join(', ')
    }
    // Legacy fallback removed - new API uses subhaulers[] array
    return 'Unassigned'
  }

  // Enhanced dispatchers name display with fallback
  const getDispatchersName = (workOrder: WorkOrder) => {
    // NEW: Display multiple dispatchers
    if (workOrder.dispatchers && workOrder.dispatchers.length > 0) {
      return workOrder.dispatchers
        .map((user) => {
          const fullName = `${user.first_name} ${user.last_name}`.trim()
          return fullName || user.email || 'Unknown'
        })
        .join(', ')
    }
    // Legacy fallback removed - new API uses dispatchers[] array
    return 'Unassigned'
  }

  // Get created by name using the fetched creator names
  const getCreatedByName = (workOrder: WorkOrder) => {
    if (!workOrder.created_by) return 'Unknown'

    // If created_by is a User object, use it directly
    if (typeof workOrder.created_by === 'object') {
      const fullName =
        `${workOrder.created_by.first_name} ${workOrder.created_by.last_name}`.trim()
      return fullName || workOrder.created_by.email || 'Unknown'
    }

    // Fallback to the creatorNames map (for backward compatibility)
    return creatorNames[workOrder.created_by] || `User ${workOrder.created_by}`
  }

  // Format start date for display
  const formatStartDate = (startDate?: string) => {
    if (!startDate) return 'Not set'
    try {
      const date = new Date(startDate)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    } catch (error) {
      console.error('Error formatting start date:', error)
      return 'Invalid date'
    }
  }

  // Format completion date for display
  const formatCompletionDate = (completionDate?: string) => {
    if (!completionDate) return 'Not set'
    try {
      const date = new Date(completionDate)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    } catch (error) {
      console.error('Error formatting completion date:', error)
      return 'Invalid date'
    }
  }

  // Map WorkOrderStatus to TagStatus for StatusBadge compatibility
  const mapWorkOrderStatusToTagStatus = (status?: string): TagStatus => {
    if (!status) return 'NEW'
    const upperStatus = status.toUpperCase()
    switch (upperStatus) {
      case 'PENDING':
        return 'NEW'
      case 'ACCEPTED':
        return 'ACCEPTED'
      case 'COMPLETE':
        return 'COMPLETE'
      default:
        return 'NEW'
    }
  }

  return (
    <>
      <tr>
        <td colSpan={columnsCount}>
          <div className={css(styles.subComponent)}>
            <div className={css(styles.expandHeader)}>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className={css(styles.expandButton)}
                title={
                  isExpanded ? 'Collapse work orders' : 'Expand work orders'
                }
              >
                <i
                  className={`fa-solid ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}
                />
                <span className={css(styles.expandText)}>
                  Dispatches ({relatedWorkOrders.length})
                </span>
              </button>
            </div>
            {isExpanded && (
              <div className={css(styles.expandableContent)}>
                <div className={css(styles.horizontalScroll)}>
                  {/* Column Headers */}
                  <div className={css(styles.columnHeaders)}>
                    <div
                      className={css(styles.columnHeader, styles.actionColumn)}
                    >
                      Action
                    </div>
                    <div
                      className={css(
                        styles.columnHeader,
                        styles.startDateColumn,
                      )}
                    >
                      Start Date
                    </div>
                    <div
                      className={css(
                        styles.columnHeader,
                        styles.completionDateColumn,
                      )}
                    >
                      Completion Date
                    </div>
                    <div
                      className={css(styles.columnHeader, styles.labelColumn)}
                    >
                      Dispatch ID
                    </div>
                    <div
                      className={css(styles.columnHeader, styles.statusColumn)}
                    >
                      Status
                    </div>
                    <div
                      className={css(
                        styles.columnHeader,
                        styles.subhaulerColumn,
                      )}
                    >
                      Sub Hauler
                    </div>
                    <div
                      className={css(
                        styles.columnHeader,
                        styles.dispatcherColumn,
                      )}
                    >
                      Dispatcher
                    </div>
                    <div
                      className={css(
                        styles.columnHeader,
                        styles.createdByColumn,
                      )}
                    >
                      Created By
                    </div>
                    <div
                      className={css(
                        styles.columnHeader,
                        styles.materialColumn,
                      )}
                    >
                      Material
                    </div>
                    <div
                      className={css(
                        styles.columnHeader,
                        styles.declineReasonColumn,
                      )}
                    >
                      Decline Reason
                    </div>
                  </div>

                  {/* Work Order Rows */}
                  {relatedWorkOrders.map((workOrder) => (
                    <div
                      key={workOrder.id}
                      className={css(styles.workOrderRow)}
                    >
                      <div className={css(styles.workOrderInfo)}>
                        <div className={css(styles.actionColumn)}>
                          <button
                            onClick={() => handleWorkOrderAction(workOrder)}
                            disabled={isButtonDisabled(workOrder)}
                            className={css(
                              styles.viewButton,
                              isButtonDisabled(workOrder) &&
                                styles.disabledButton,
                            )}
                            title="View Dispatch"
                          >
                            <i className="fa-solid fa-eye" />
                          </button>
                        </div>
                        <div className={css(styles.startDateColumn)}>
                          <TruncatableCell
                            content={formatStartDate(workOrder.starting_date)}
                            maxWidth="100px"
                          />
                        </div>
                        <div className={css(styles.completionDateColumn)}>
                          <TruncatableCell
                            content={formatCompletionDate(
                              workOrder.completion_date,
                            )}
                            maxWidth="150px"
                          />
                        </div>
                        <div className={css(styles.labelColumn)}>
                          <span className={css(styles.workOrderLabel)}>
                            {workOrder.label || '-'}
                          </span>
                        </div>
                        <div className={css(styles.statusColumn)}>
                          <StatusBadge
                            status={mapWorkOrderStatusToTagStatus(
                              workOrder.status,
                            )}
                          />
                        </div>
                        <div className={css(styles.subhaulerColumn)}>
                          <TruncatableCell
                            content={getSubHaulersName(workOrder)}
                            maxWidth="120px"
                          />
                        </div>
                        <div className={css(styles.dispatcherColumn)}>
                          <TruncatableCell
                            content={getDispatchersName(workOrder)}
                            maxWidth="120px"
                          />
                        </div>
                        <div className={css(styles.createdByColumn)}>
                          <TruncatableCell
                            content={getCreatedByName(workOrder)}
                            maxWidth="120px"
                          />
                        </div>
                        <div className={css(styles.materialColumn)}>
                          <TruncatableCell
                            content={workOrder.material || 'Not specified'}
                            maxWidth="120px"
                          />
                        </div>
                        <div className={css(styles.declineReasonColumn)}>
                          <TruncatableCell
                            content={
                              workOrder.decline_reason
                                ? workOrder.decline_reason.length > 50
                                  ? `${workOrder.decline_reason.substring(0, 50)}...`
                                  : workOrder.decline_reason
                                : '-'
                            }
                            maxWidth="120px"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </td>
      </tr>

      {viewingWorkOrder && (
        <ViewWorkOrderModal
          workOrder={viewingWorkOrder}
          onClose={() => setViewingWorkOrder(null)}
        />
      )}
    </>
  )
}

const styles = StyleSheet.create({
  subComponent: {
    padding: '8px 0px 8px 40px',
    backgroundColor: '#F9FAFB',
    borderBottom: '1px solid #E5E7EB',
    width: '100%',
  },
  horizontalScroll: {
    overflowX: 'auto',
    width: '100%',
    paddingBottom: 8,
  },
  workOrderRow: {
    padding: '8px 12px',
    borderLeft: '2px solid #059669', // Green left border to distinguish from tags
    marginBottom: '4px',
    backgroundColor: 'white',
    borderRadius: '4px',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  },
  workOrderInfo: {
    display: 'grid',
    gridTemplateColumns:
      '60px 100px 150px 120px 120px 120px 120px 120px 120px 120px',
    alignItems: 'center',
    fontSize: '13px',
    gap: '8px',
    width: '100%',
    minWidth: 1150,
    '@media (max-width: 1024px)': {
      gridTemplateColumns:
        '50px 90px 140px 120px 120px 120px 120px 120px 120px 120px',
      fontSize: '12px',
      gap: '6px',
      minWidth: 1060,
    },
  },
  viewButton: {
    padding: '4px 8px',
    borderRadius: '4px',
    border: '1px solid #E5E7EB',
    backgroundColor: 'white',
    cursor: 'pointer',
    color: '#6B7280',
    width: '40px',
    minWidth: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ':hover': {
      backgroundColor: '#F3F4F6',
      color: '#059669',
    },
  },
  disabledButton: {
    backgroundColor: '#F9FAFB',
    color: '#D1D5DB',
    cursor: 'not-allowed',
    border: '1px solid #E5E7EB',
    ':hover': {
      backgroundColor: '#F9FAFB',
      color: '#D1D5DB',
    },
  },
  // Column Headers
  columnHeaders: {
    display: 'grid',
    gridTemplateColumns:
      '60px 100px 150px 120px 120px 120px 120px 120px 120px 120px',
    alignItems: 'center',
    fontSize: '12px',
    fontWeight: 600,
    color: '#374151',
    padding: '8px 12px',
    backgroundColor: '#F3F4F6',
    borderBottom: '1px solid #E5E7EB',
    gap: '8px',
    width: '100%',
    minWidth: 1150,
    '@media (max-width: 1024px)': {
      gridTemplateColumns:
        '50px 90px 140px 120px 120px 120px 120px 120px 120px 120px',
      minWidth: 1060,
    },
  },
  columnHeader: {
    textAlign: 'left',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },

  // Column Layout
  actionColumn: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  labelColumn: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  statusColumn: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  subhaulerColumn: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  dispatcherColumn: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  createdByColumn: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  materialColumn: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  declineReasonColumn: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  startDateColumn: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  completionDateColumn: {
    display: 'flex',
    justifyContent: 'flex-start',
  },

  // Content Styles
  workOrderLabel: {
    fontWeight: 600,
    color: '#059669',
    textAlign: 'left',
  },
  columnContent: {
    color: '#6B7280',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: '0',
  },
  expandHeader: {
    padding: '8px 16px',
    backgroundColor: '#F9FAFB',
  },
  expandButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#6B7280',
    fontSize: '13px',
    fontWeight: 500,
    padding: '4px 0',
    ':hover': {
      color: '#059669',
    },
  },
  expandText: {
    fontSize: '13px',
    fontWeight: 500,
  },
  expandableContent: {
    paddingTop: '8px',
  },
  errorMessage: {
    padding: '16px',
    backgroundColor: '#FEF2F2',
    border: '1px solid #FEE2E2',
    borderRadius: '6px',
    color: '#991B1B',
    margin: '8px 40px',
    '& h3': {
      margin: '0 0 8px 0',
      fontSize: '14px',
      fontWeight: 600,
    },
    '& p': {
      margin: 0,
      fontSize: '13px',
    },
  },
})

export default WorkOrderSubRow
